import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  Observable,
  firstValueFrom,
  from,
  map,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthSession, BoAuthUser } from '../models/auth.model';
import { AuthStore } from '../stores/auth.store';
import { authFlowLog } from './auth-flow-log';
import { BoAuthApiService } from './bo-auth-api.service';
import {
  challengeFromVerifier,
  generateCodeVerifier,
  generateNonce,
  randomState,
} from './oidc-pkce.utility';
import { RefreshTokenReuseError } from './refresh-token-reuse.error';
import { SessionCookieService } from './session-cookie.service';

const DEFAULT_ACCESS_TOKEN_TTL_SECONDS = 600;

const ROTATION_CHECK_INTERVAL_MS = 60_000;

const ROTATE_AFTER_MS = 5 * 60_000;

const REUSE_DETECTION_WINDOW_MS = 15 * 60_000;

const REFRESH_LOCK_NAME = 'dv-refresh';

const E2E_TOKEN_PREFIX = 'e2e:';

const LOCAL_LOGIN_VERIFIER_KEY = 'bo_local_login_verifier';
const LOCAL_LOGIN_STATE_KEY = 'bo_local_login_state';
const LOCAL_LOGIN_RETURN_URL_KEY = 'bo_local_login_return_url';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private readonly authStore = inject(AuthStore);
  private readonly boAuthApi = inject(BoAuthApiService);
  private readonly cookies = inject(SessionCookieService);

  private lastWrittenRefreshToken: string | null = null;
  private lastWrittenRefreshAt = 0;
  private rotationTimer: ReturnType<typeof setInterval> | null = null;

  public ngOnDestroy(): void {
    this.stopRotation();
  }

  private log(step: string, detail?: Record<string, unknown>): void {
    authFlowLog(environment.debug, step, detail);
  }

  public getAccessToken(): string | null {
    return this.cookies.read();
  }

  public isAuthenticated(): boolean {
    return this.authStore.isAuthenticated();
  }

  public isE2eToken(token: string | null = this.getAccessToken()): boolean {
    return token !== null && token.startsWith(E2E_TOKEN_PREFIX);
  }

  public hasValidToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    if (this.isE2eToken(token)) {
      return true;
    }

    const expiry = AuthService.expiryOf(token);
    return expiry === null || expiry > Date.now();
  }

  public loadSession(): Observable<AuthSession> {
    const token = this.getAccessToken();

    if (!token) {
      this.log('1-cookie:absent');
      return throwError(() => new Error('No session cookie'));
    }

    if (
      this.isE2eToken(token) ||
      AuthService.expiryOf(token) === null ||
      AuthService.expiryOf(token)! > Date.now()
    ) {
      return this.resolveSession(token);
    }

    this.log('1-cookie:expired', {
      hasRefreshToken: !!this.cookies.readRefreshToken(),
    });

    return from(this.refresh()).pipe(
      switchMap(() => {
        const refreshed = this.getAccessToken();
        if (!refreshed) {
          return throwError(() => new Error('Refresh produced no token'));
        }
        return this.resolveSession(refreshed);
      }),
      catchError((error: unknown) => {
        this.authStore.clearAuth();
        return throwError(() => error);
      })
    );
  }

  public refresh(): Promise<void> {
    if (this.isE2eToken()) {
      return Promise.reject(new Error('E2E test token cannot be refreshed'));
    }

    return this.underRefreshLock(() => this.rotate());
  }

  public redirectToPortal(): void {
    globalThis.location.href = environment.servicesPortalUrl;
  }

  public async startLocalLogin(returnUrl: string): Promise<void> {
    const url = await this.localLoginUrl(returnUrl);
    if (url) {
      globalThis.location.href = url;
    }
  }

  public async localLoginUrl(returnUrl: string): Promise<string | null> {
    if (!environment.allowLocalOneLoginLogin) {
      return null;
    }

    const verifier = generateCodeVerifier();
    const state = randomState();
    sessionStorage.setItem(LOCAL_LOGIN_VERIFIER_KEY, verifier);
    sessionStorage.setItem(LOCAL_LOGIN_STATE_KEY, state);
    sessionStorage.setItem(LOCAL_LOGIN_RETURN_URL_KEY, returnUrl);

    const parameters = new URLSearchParams({
      // eslint-disable-next-line camelcase
      client_id: environment.oneLogin.clientId,
      // eslint-disable-next-line camelcase
      redirect_uri: environment.oneLogin.redirectUri,
      // eslint-disable-next-line camelcase
      response_type: 'code',
      scope: environment.oneLogin.scopes,
      state,
      nonce: generateNonce(),
      // eslint-disable-next-line camelcase
      code_challenge: await challengeFromVerifier(verifier),
      // eslint-disable-next-line camelcase
      code_challenge_method: 'S256',
    });

    return `${environment.oneLogin.issuer}/auth?${parameters.toString()}`;
  }

  public async handleLocalCallback(
    code: string,
    state: string
  ): Promise<string> {
    if (!environment.allowLocalOneLoginLogin) {
      throw new Error('Local OneLogin login is disabled in this environment');
    }

    const expectedState = sessionStorage.getItem(LOCAL_LOGIN_STATE_KEY);
    const verifier = sessionStorage.getItem(LOCAL_LOGIN_VERIFIER_KEY);
    const returnUrl = sessionStorage.getItem(LOCAL_LOGIN_RETURN_URL_KEY) ?? '/';

    if (!state || state !== expectedState || !verifier) {
      throw new Error('Invalid authentication state');
    }

    this.log('L1-local-login:posting-to-onelogin');

    const body = new URLSearchParams({
      // eslint-disable-next-line camelcase
      grant_type: 'authorization_code',
      // eslint-disable-next-line camelcase
      client_id: environment.oneLogin.clientId,
      code,
      // eslint-disable-next-line camelcase
      redirect_uri: environment.oneLogin.redirectUri,
      // eslint-disable-next-line camelcase
      code_verifier: verifier,
    });

    const response = await fetch(`${environment.oneLogin.issuer}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      throw new Error(`OneLogin authorization_code failed: ${response.status}`);
    }

    const tokens = (await response.json()) as {
      access_token: string;
      refresh_token?: string;
      id_token?: string;
      expires_in?: number;
    };

    this.log('L2-local-login:token-received', {
      hasRefreshToken: !!tokens.refresh_token,
    });

    this.cookies.write(
      tokens.access_token,
      tokens.expires_in ?? DEFAULT_ACCESS_TOKEN_TTL_SECONDS
    );

    if (tokens.id_token) {
      this.cookies.writeIdToken(tokens.id_token);
    }

    if (tokens.refresh_token) {
      this.cookies.writeRefreshToken(tokens.refresh_token);
      this.lastWrittenRefreshToken = tokens.refresh_token;
      this.lastWrittenRefreshAt = Date.now();
    }

    sessionStorage.removeItem(LOCAL_LOGIN_VERIFIER_KEY);
    sessionStorage.removeItem(LOCAL_LOGIN_STATE_KEY);
    sessionStorage.removeItem(LOCAL_LOGIN_RETURN_URL_KEY);

    await firstValueFrom(this.loadSession());

    return returnUrl;
  }

  public logout(): void {
    const logoutUrl = this.buildOneLoginLogoutUrl();
    this.stopRotation();
    this.authStore.clearAuth();
    this.cookies.clear();

    if (logoutUrl) {
      globalThis.location.href = logoutUrl;
      return;
    }

    this.redirectToPortal();
  }

  public buildOneLoginLogoutUrl(): string {
    const idToken = this.cookies.readIdToken();
    if (!idToken) {
      return '';
    }

    const postLogoutRedirectUri = environment.allowLocalOneLoginLogin
      ? globalThis.location.origin
      : environment.servicesPortalUrl;

    const parameters = new URLSearchParams({
      // eslint-disable-next-line camelcase
      id_token_hint: idToken,
      // eslint-disable-next-line camelcase
      post_logout_redirect_uri: postLogoutRedirectUri,
    });
    return `${environment.oneLogin.issuer}/logout?${parameters.toString()}`;
  }

  public getCurrentUser(): AuthSession['user'] | null {
    return this.authStore.user();
  }

  public getCurrentClient(): string | null {
    return this.authStore.getCurrentClient();
  }

  private resolveSession(token: string): Observable<AuthSession> {
    this.log('2-bo-api:requesting-me');

    return this.boAuthApi.me(token).pipe(
      tap(user =>
        this.log('3-bo-api:me-received', {
          club: user.club,
          role: user.role,
          services: user.services.length,
        })
      ),
      map(user => AuthService.toSession(token, user)),
      tap(session => this.authStore.setAuthenticated(session)),
      tap(() => this.startRotation())
    );
  }

  private async rotate(): Promise<void> {
    const sent = this.cookies.readRefreshToken();
    if (!sent) {
      throw new Error('No refresh token');
    }

    this.log('R1-refresh:posting-to-onelogin');

    let response = await AuthService.postRefreshGrant(sent);

    if (!response.ok) {
      const current = this.cookies.readRefreshToken();
      if (current && current !== sent) {
        this.log('R1b-refresh:retrying-with-cookie-rotated-elsewhere');
        response = await AuthService.postRefreshGrant(current);
      } else if (
        sent === this.lastWrittenRefreshToken &&
        Date.now() - this.lastWrittenRefreshAt <= REUSE_DETECTION_WINDOW_MS &&
        (await AuthService.isInvalidGrant(response))
      ) {
        throw new RefreshTokenReuseError();
      }
    }

    if (!response.ok) {
      throw new Error(`OneLogin refresh_token failed: ${response.status}`);
    }

    const tokens = (await response.json()) as {
      access_token: string;
      refresh_token?: string;
      id_token?: string;
      expires_in?: number;
    };

    this.log('R2-refresh:new-token-received', {
      rotatedRefreshToken: !!tokens.refresh_token,
    });

    this.cookies.write(
      tokens.access_token,
      tokens.expires_in ?? DEFAULT_ACCESS_TOKEN_TTL_SECONDS
    );

    if (tokens.id_token) {
      this.cookies.writeIdToken(tokens.id_token);
    }

    if (tokens.refresh_token) {
      this.cookies.writeRefreshToken(tokens.refresh_token);
      this.lastWrittenRefreshToken = tokens.refresh_token;
      this.lastWrittenRefreshAt = Date.now();
    }
  }

  private async underRefreshLock(work: () => Promise<void>): Promise<void> {
    const locks = globalThis.navigator?.locks;

    try {
      await (locks ? locks.request(REFRESH_LOCK_NAME, work) : work());
    } catch (error) {
      if (error instanceof RefreshTokenReuseError) {
        this.log('R3-refresh:reuse-detected');
        await firstValueFrom(this.boAuthApi.reportRefreshReuse()).catch(
          (reportError: unknown) =>
            this.log('R3b-refresh:reuse-report-failed', {
              error: String(reportError),
            })
        );
      }
      throw error;
    }
  }

  private startRotation(): void {
    if (this.rotationTimer || this.isE2eToken()) {
      return;
    }

    this.rotationTimer = setInterval(
      this.onRotationTick,
      ROTATION_CHECK_INTERVAL_MS
    );
    document.addEventListener('visibilitychange', this.onRotationTick);
  }

  private stopRotation(): void {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }

    document.removeEventListener('visibilitychange', this.onRotationTick);
  }

  private readonly onRotationTick = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    this.underRefreshLock(async () => {
      if (this.isRotationDue()) {
        await this.rotate();
      }
    }).catch((error: unknown) => {
      if (error instanceof RefreshTokenReuseError) {
        this.logout();
        return;
      }
      this.log('R4-refresh:background-failed', { error: String(error) });
    });
  };

  private isRotationDue(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return !!this.cookies.readRefreshToken();
    }

    const issuedAt = AuthService.claimsOf(token)?.['iat'];
    if (typeof issuedAt === 'number') {
      return Date.now() - issuedAt * 1000 >= ROTATE_AFTER_MS;
    }

    const expiry = AuthService.expiryOf(token);
    return expiry !== null && expiry - Date.now() <= ROTATE_AFTER_MS;
  }

  private static postRefreshGrant(refreshToken: string): Promise<Response> {
    const body = new URLSearchParams({
      // eslint-disable-next-line camelcase
      grant_type: 'refresh_token',
      // eslint-disable-next-line camelcase
      client_id: environment.oneLogin.clientId,
      // eslint-disable-next-line camelcase
      refresh_token: refreshToken,
    });

    return fetch(`${environment.oneLogin.issuer}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  }

  private static async isInvalidGrant(response: Response): Promise<boolean> {
    if (response.status !== 400) {
      return false;
    }

    const body = (await response
      .clone()
      .json()
      .catch(() => null)) as { error?: string } | null;
    return body?.error === 'invalid_grant';
  }

  private static toSession(token: string, user: BoAuthUser): AuthSession {
    return {
      token,
      user: {
        fullName: `${user.firstname} ${user.lastname}`.trim(),
        email: user.email,
      },
      club: user.club,
      clientName: user.clientName,
      tags: user.role ? [user.role] : [],
      services: user.services,
    };
  }

  private static expiryOf(token: string): number | null {
    const claims = AuthService.claimsOf(token);
    return typeof claims?.['exp'] === 'number' ? claims['exp'] * 1000 : null;
  }

  private static claimsOf(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length < 3) {
      return null;
    }

    try {
      return JSON.parse(
        atob(parts[1].replaceAll('-', '+').replaceAll('_', '/'))
      ) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
